/**
 * SVG Icon Component (React port of Angular `SvgIconComponent`).
 *
 * Wrapper component for displaying SVG icons from the public/assets/svg-icons
 * directory. Replaces lucide with an inline-SVG approach for full color control.
 *
 * Features (1:1 with Angular):
 * - Inlines SVG content for CSS color styling
 * - Removes hardcoded stroke/fill colors from SVG files
 * - Supports `currentColor` for dynamic color inheritance
 * - Works with Tailwind `text-*` utility classes
 *
 * Usage:
 *   <SvgIcon name="user" size="medium" alt="User profile" />
 *   <SvgIcon name="calendar" size="small" className="text-white" />
 *   <SvgIcon name="heart" size="large" className="text-red-500" />
 *
 * Sizes: small (18px), medium (24px, default), large (32px).
 *
 * @see iconService for available icon names.
 */

import { useEffect, useMemo, useState } from 'react';

import { iconService } from '@/services/icon/icon.service';

import './SvgIcon.scss';

export interface SvgIconProps {
  /** Icon name (e.g. 'user', 'calendar', 'heart'). Maps to an SVG file via the icon service. */
  name: string;
  /** Alt text for accessibility. If empty, the icon is rendered as `aria-hidden`. */
  alt?: string;
  /** Icon size: 'small' (18px), 'medium' (24px), or 'large' (32px). */
  size?: 'small' | 'medium' | 'large';
  /** Extra classes applied to the host (e.g. Tailwind `text-*` for color). */
  className?: string;
}

/**
 * Clean up SVG markup: remove hardcoded stroke/fill colors so `currentColor`
 * can drive the color, and add `preserveAspectRatio` for proper scaling.
 * Reproduced 1:1 from the Angular `sanitizedSvg` computed.
 */
function sanitizeSvg(svg: string): string {
  if (!svg) return '';

  return (
    svg
      // Remove hardcoded stroke colors (but keep stroke-width, stroke-linecap, etc.)
      .replace(/stroke="#[A-Fa-f0-9]{6}"/g, '')
      .replace(/stroke='#[A-Fa-f0-9]{6}'/g, '')
      // Remove hardcoded fill colors (except fill="none")
      .replace(/fill="(?!none)[^"]+"/g, '')
      .replace(/fill='(?!none)[^']+'/g, '')
      // Ensure proper scaling
      .replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet"')
  );
}

/** Renders a named SVG icon by inlining its (color-stripped) markup. */
export function SvgIcon({ name, alt = '', size = 'medium', className }: SvgIconProps) {
  const [svgContent, setSvgContent] = useState<string>('');

  const iconPath = useMemo(() => iconService.getIconPath(name), [name]);
  const iconClass = `icon-${size}`;
  const sanitizedSvg = useMemo(() => sanitizeSvg(svgContent), [svgContent]);

  useEffect(() => {
    if (!iconPath) return;

    let cancelled = false;

    fetch(iconPath)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((svg) => {
        if (!cancelled) setSvgContent(svg);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`[SvgIcon] Failed to load icon "${name}" from ${iconPath}:`, err);
        setSvgContent('');
      });

    return () => {
      cancelled = true;
    };
  }, [iconPath, name]);

  const hostClass = className ? `svg-icon ${className}` : 'svg-icon';

  return (
    <span className={hostClass}>
      <div
        className={`svg-icon-wrapper ${iconClass}`}
        aria-label={alt || undefined}
        role={alt ? 'img' : undefined}
        aria-hidden={alt ? undefined : true}
        dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
      />
    </span>
  );
}

export default SvgIcon;
